/**
 * "Any column is a field": a working segment builder over example data — the
 * owner's brief for slice 67 ("it can be any custom column user wants to add
 * to the rule … also rule based segment"), shown rather than claimed.
 *
 * ## WHAT IS REAL AND WHAT IS AN EXAMPLE
 *
 * The rule vocabulary is the product's: the same field types, the same
 * operators per type in the same order, the same words, ALL/ANY groups one
 * level deep inside the root, and a rule with no value yet ignored and saying
 * "needs a value" rather than silently matching nobody. `lib/segment-demo.ts`
 * says how the site suite keeps that copy in step with
 * `packages/shared/src/contracts/rules.ts`.
 *
 * The people are not anybody's. They are 240 made-up rows generated in the
 * browser from a fixed seed, and the panel's first line says so ("made-up
 * people, generated in your browser · nothing is sent") before anything else. Nothing on this section makes a request.
 *
 * ## THE AGE SUGGESTION
 *
 * The catalogue offers "Add Age from Birthday" because the column's name
 * contains "birth" — the product's own rule — and only offers it: Age does not
 * exist until the reader presses the button, as in the product, where a
 * suggestion is confirmed with one click and never created on its own. "Add a
 * derived field" then shows the general case: years since, days since or month
 * of ANY date column (Joining date, Last order), which is the point of the
 * brief — Age is one use of a general tool, not a special field.
 *
 * ## A SIMPLER PICKER THAN THE PRODUCT'S
 *
 * The product's field picker is a searchable popover with coverage bars. Here
 * it is a native `<select>` grouped by Profile / Your columns / Derived /
 * Activity (coverage is on the column chips above it). A page read once on a phone is
 * better served by the control the phone already knows, and a native select is
 * fully keyboard- and screen-reader-operable without a line of focus code.
 *
 * ## LIVE COUNTS
 *
 * Every change recounts synchronously (240 rows; there is nothing to
 * debounce). The total and the reachable counts sit in a polite live region,
 * so a screen reader hears the new figure after an edit, once. Per-rule counts
 * are not live — they would announce on every keystroke — and each says in its
 * accessible name what it counts.
 */
import { useId, useMemo, useState, type ReactNode } from 'react';

import { cn } from '@wend/ui';

import {
  DATE_COLUMNS,
  DEMO_PEOPLE_COUNT,
  DERIVED_KIND_LABEL,
  FIELD_GROUP_LABEL,
  OPERATORS_BY_TYPE,
  ageSuggestions,
  buildCatalogue,
  countRules,
  defaultValue,
  derivedFieldFor,
  formatCount,
  initialRules,
  isComplete,
  nextId,
  operatorLabel,
  removeNode,
  updateNode,
  type DemoField,
  type DemoGroup,
  type DemoNode,
  type DemoRule,
  type DerivedField,
  type DerivedKind,
  type FieldGroup,
  type RuleOperator,
  type RuleValue,
  type Window,
} from '../lib/segment-demo';

const focus = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b';
const control = cn('h-9 rounded-sm border border-line-strong bg-surface-2 px-2.5 text-[0.85rem] text-text', focus);
const smallButton = cn(
  'inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-white/[0.04] px-3 py-1.5 text-[0.8rem] text-text transition-colors hover:bg-white/[0.09]',
  focus,
);
const iconButton = cn('grid size-8 flex-none place-items-center rounded-sm text-muted transition-colors hover:bg-bad/12 hover:text-bad', focus);

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function JoinToggle({ join, onChange, label }: { join: 'and' | 'or'; onChange: (join: 'and' | 'or') => void; label: string }) {
  return (
    <span role="group" aria-label={label} className="inline-flex rounded-full border border-line-strong bg-black/30 p-[3px]">
      {(['and', 'or'] as const).map((value) => (
        <button
          key={value}
          type="button"
          aria-pressed={join === value}
          onClick={() => onChange(value)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.8rem] transition-colors',
            focus,
            join === value ? 'bg-brand-a/32 text-white shadow-[inset_0_0_0_1px_rgba(155,94,246,0.5)]' : 'text-muted hover:text-text',
          )}
        >
          {value === 'and' ? 'ALL' : 'ANY'} <small className="font-mono text-[0.66rem] opacity-75">{value === 'and' ? 'AND' : 'OR'}</small>
        </button>
      ))}
    </span>
  );
}

function NumberInput({ value, onChange, label, className }: { value: number | ''; onChange: (value: number | '') => void; label: string; className?: string }) {
  return (
    <input
      type="number"
      inputMode="numeric"
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value === '' ? '' : Number(event.target.value))}
      className={cn(control, 'w-20', className)}
    />
  );
}

function ValueEditor({ rule, field, onChange }: { rule: DemoRule; field: DemoField; onChange: (value: RuleValue) => void }) {
  const { op, value } = rule;
  if (op === 'known' || op === 'unknown' || op === 'is_true' || op === 'is_false' || op === 'ever' || op === 'never') return null;

  if (op === 'in_last' || op === 'not_in_last') {
    const window = (value as Window | null) ?? { amount: 30, unit: 'days' };
    return (
      <span className="inline-flex items-center gap-1.5">
        <NumberInput value={window.amount} label={`How many, for ${field.label}`} onChange={(amount) => onChange({ ...window, amount })} />
        <select
          aria-label={`Days or months, for ${field.label}`}
          value={window.unit}
          onChange={(event) => onChange({ ...window, unit: event.target.value === 'months' ? 'months' : 'days' })}
          className={control}
        >
          <option value="days">days</option>
          <option value="months">months</option>
        </select>
      </span>
    );
  }

  if (op === 'anniversary_in_next') {
    const days = (value as { days: number | '' } | null)?.days ?? 30;
    return (
      <span className="inline-flex items-center gap-1.5">
        <NumberInput value={days} label={`Days ahead, for ${field.label}`} onChange={(next) => onChange({ days: next })} />
        <span className="text-[0.82rem] text-muted">days</span>
      </span>
    );
  }

  if (op === 'between') {
    const pair = (Array.isArray(value) ? value : ['', '']) as unknown as readonly [number | string, number | string];
    if (field.type === 'date') {
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <input type="date" aria-label={`${field.label} from`} value={String(pair[0])} onChange={(event) => onChange([event.target.value, String(pair[1])])} className={control} />
          <span className="text-[0.82rem] text-muted">and</span>
          <input type="date" aria-label={`${field.label} to`} value={String(pair[1])} onChange={(event) => onChange([String(pair[0]), event.target.value])} className={control} />
        </span>
      );
    }
    const low = pair[0] === '' ? '' : Number(pair[0]);
    const high = pair[1] === '' ? '' : Number(pair[1]);
    return (
      <span className="inline-flex items-center gap-1.5">
        <NumberInput value={low} label={`${field.label} from`} onChange={(next) => onChange([next, high])} />
        <span className="text-[0.82rem] text-muted">and</span>
        <NumberInput value={high} label={`${field.label} to`} onChange={(next) => onChange([low, next])} />
      </span>
    );
  }

  if (op === 'before' || op === 'after') {
    return <input type="date" aria-label={`${field.label} date`} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} className={control} />;
  }

  if (op === 'any_of') {
    const chosen = Array.isArray(value) ? (value as readonly string[]) : [];
    const rest = (field.values ?? []).filter((option) => !chosen.includes(option));
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {chosen.map((option) => (
          <span key={option} className="inline-flex items-center gap-1 rounded-full border border-brand-b/40 bg-brand-a/16 py-0.5 pl-2.5 pr-1 text-[0.8rem] text-brand-ink-strong">
            {option}
            <button type="button" aria-label={`Remove ${option}`} onClick={() => onChange(chosen.filter((item) => item !== option))} className={cn('grid size-5 place-items-center rounded-full hover:bg-white/10', focus)}>
              <CloseIcon />
            </button>
          </span>
        ))}
        {field.values !== null ? (
          rest.length > 0 && (
            <select aria-label={`Add a value for ${field.label}`} value="" onChange={(event) => event.target.value !== '' && onChange([...chosen, event.target.value])} className={cn(control, 'w-24')}>
              <option value="">＋ Add</option>
              {rest.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )
        ) : (
          <input
            aria-label={`${field.label} values, separated by commas`}
            placeholder="value, value"
            defaultValue={chosen.join(', ')}
            onChange={(event) => onChange(event.target.value.split(',').map((item) => item.trim()).filter((item) => item !== ''))}
            className={cn(control, 'w-40')}
          />
        )}
      </span>
    );
  }

  if ((op === 'is' || op === 'is_not') && field.values !== null) {
    return (
      <select aria-label={`${field.label} value`} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} className={control}>
        {field.values.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'number') {
    return <NumberInput value={typeof value === 'number' ? value : ''} label={`${field.label} value`} onChange={onChange} />;
  }

  return <input aria-label={`${field.label} value`} placeholder="value" value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} className={cn(control, 'w-40')} />;
}

const GROUP_ORDER: readonly FieldGroup[] = ['profile', 'columns', 'derived', 'activity'];

function FieldSelect({ value, catalogue, onChange, label }: { value: string; catalogue: readonly DemoField[]; onChange: (key: string) => void; label: string }) {
  return (
    <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} className={cn(control, 'max-w-[13rem] font-medium')}>
      {GROUP_ORDER.map((group) => {
        const fields = catalogue.filter((field) => field.group === group);
        if (fields.length === 0) return null;
        return (
          <optgroup key={group} label={FIELD_GROUP_LABEL[group]}>
            {fields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

function RuleRow({
  rule,
  field,
  count,
  catalogue,
  onChange,
  onRemove,
}: {
  rule: DemoRule;
  field: DemoField;
  count: number;
  catalogue: readonly DemoField[];
  onChange: (rule: DemoRule) => void;
  onRemove: () => void;
}) {
  const complete = isComplete(rule);
  const ops = OPERATORS_BY_TYPE[field.type];
  return (
    <div className={cn('rounded-base border bg-white/[0.035] px-3 py-2.5', complete ? 'border-line' : 'border-dashed border-warn/45')}>
      <div className="flex flex-wrap items-center gap-2">
        <FieldSelect
          label="Field"
          value={rule.field}
          catalogue={catalogue}
          onChange={(key) => {
            const next = catalogue.find((candidate) => candidate.key === key);
            if (next === undefined) return;
            const op = OPERATORS_BY_TYPE[next.type][0] as RuleOperator;
            onChange({ ...rule, field: key, op, value: defaultValue(next, op) });
          }}
        />
        <select
          aria-label={`Operator for ${field.label}`}
          value={rule.op}
          onChange={(event) => {
            const op = event.target.value as RuleOperator;
            onChange({ ...rule, op, value: defaultValue(field, op) });
          }}
          className={control}
        >
          {ops.map((op) => (
            <option key={op} value={op}>
              {operatorLabel(op, field.type)}
            </option>
          ))}
        </select>
        <ValueEditor rule={rule} field={field} onChange={(value) => onChange({ ...rule, value })} />
        <span className="ml-auto flex items-center gap-1.5">
          <span
            className={cn('rounded-full px-2 py-0.5 text-[0.78rem] font-semibold tabular-nums', complete ? 'bg-brand-a/18 text-brand-ink' : 'text-warn')}
            aria-label={complete ? `${formatCount(count)} people match this rule on its own` : 'This rule needs a value and is ignored until it has one'}
          >
            {complete ? formatCount(count) : 'needs a value'}
          </span>
          <button type="button" aria-label={`Remove rule: ${field.label} ${operatorLabel(rule.op, field.type)}`} onClick={onRemove} className={iconButton}>
            <CloseIcon />
          </button>
        </span>
      </div>
      {field.note !== undefined && (
        <p className="mt-1.5 text-[0.76rem] text-muted">
          <span className="mr-1.5 rounded-sm bg-white/[0.08] px-1.5 py-px font-mono text-[0.64rem] uppercase tracking-[0.08em]">{field.group === 'derived' ? 'derived' : 'activity'}</span>
          {field.note}
        </p>
      )}
    </div>
  );
}

function GroupBox({
  group,
  depth,
  catalogue,
  perNode,
  actions,
}: {
  group: DemoGroup;
  depth: number;
  catalogue: readonly DemoField[];
  perNode: ReadonlyMap<string, number>;
  actions: {
    update: (id: string, change: (node: DemoNode) => DemoNode) => void;
    remove: (id: string) => void;
    addRule: (groupId: string) => void;
    addGroup: (groupId: string) => void;
  };
}) {
  const word = group.join === 'and' ? 'AND' : 'OR';
  const n = group.children.length;
  return (
    <div className={cn('rounded-lg border p-3 sm:p-4', depth === 0 ? 'border-line-strong bg-surface-1' : 'border-brand-b/30 bg-brand-a/[0.06]')}>
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[0.9rem] font-semibold text-text">{depth === 0 ? 'People who match' : 'Group'}</span>
        <JoinToggle
          join={group.join}
          label={depth === 0 ? 'Match all or any of the conditions' : 'Match all or any of this group'}
          onChange={(join) => actions.update(group.id, (node) => (node.kind === 'group' ? { ...node, join } : node))}
        />
        <span className="text-[0.82rem] text-muted">of {n === 1 ? 'this condition' : `these ${n} conditions`}</span>
        {depth > 0 && (
          <span className="ml-auto flex items-center gap-1.5">
            <span className="rounded-full bg-brand-a/18 px-2 py-0.5 text-[0.78rem] font-semibold tabular-nums text-brand-ink" aria-label={`${formatCount(perNode.get(group.id) ?? 0)} people match this group on its own`}>
              {formatCount(perNode.get(group.id) ?? 0)}
            </span>
            <button type="button" aria-label="Remove group" onClick={() => actions.remove(group.id)} className={iconButton}>
              <CloseIcon />
            </button>
          </span>
        )}
      </div>

      <div className="mt-3 grid gap-2">
        {n === 0 && <p className="text-[0.84rem] text-muted">No conditions, so everyone matches.</p>}
        {group.children.map((child, index) => {
          let body: ReactNode;
          if (child.kind === 'group') {
            body = <GroupBox group={child} depth={depth + 1} catalogue={catalogue} perNode={perNode} actions={actions} />;
          } else {
            const field = catalogue.find((candidate) => candidate.key === child.field);
            body =
              field === undefined ? null : (
                <RuleRow
                  rule={child}
                  field={field}
                  count={perNode.get(child.id) ?? 0}
                  catalogue={catalogue}
                  onChange={(next) => actions.update(child.id, () => next)}
                  onRemove={() => actions.remove(child.id)}
                />
              );
          }
          return (
            <div key={child.id}>
              {index > 0 && (
                <p aria-hidden="true" className={cn('my-1 pl-3 font-mono text-[0.64rem] tracking-[0.12em]', group.join === 'and' ? 'text-brand-ink' : 'text-ident-d')}>
                  {word}
                </p>
              )}
              {body}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" className={smallButton} onClick={() => actions.addRule(group.id)}>
          <span aria-hidden="true">＋</span> {depth === 0 ? 'Add rule' : `Add rule to this ${word} group`}
        </button>
        {depth === 0 && (
          <button type="button" className={smallButton} onClick={() => actions.addGroup(group.id)}>
            <span aria-hidden="true">⊞</span> Add group
          </button>
        )}
      </div>
    </div>
  );
}

function DerivedFieldForm({ onAdd, existing }: { onAdd: (field: DerivedField) => void; existing: readonly DerivedField[] }) {
  const id = useId();
  const [kind, setKind] = useState<DerivedKind>('years_since');
  const [source, setSource] = useState<string>(DATE_COLUMNS.find((column) => column === 'Joining date') ?? DATE_COLUMNS[0] ?? '');
  const preview = derivedFieldFor(kind, source);
  const already = existing.some((field) => field.key === preview.key);
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2.5 rounded-base border border-line bg-white/[0.03] p-3">
      <label className="grid gap-1 text-[0.76rem] text-muted" htmlFor={`${id}-kind`}>
        Kind
        <select id={`${id}-kind`} value={kind} onChange={(event) => setKind(event.target.value as DerivedKind)} className={control}>
          {(Object.keys(DERIVED_KIND_LABEL) as DerivedKind[]).map((option) => (
            <option key={option} value={option}>
              {DERIVED_KIND_LABEL[option]}
            </option>
          ))}
        </select>
      </label>
      <label className="grid gap-1 text-[0.76rem] text-muted" htmlFor={`${id}-source`}>
        From any date column
        <select id={`${id}-source`} value={source} onChange={(event) => setSource(event.target.value)} className={control}>
          {DATE_COLUMNS.map((column) => (
            <option key={column} value={column}>
              {column}
            </option>
          ))}
        </select>
      </label>
      <button type="button" disabled={already} onClick={() => onAdd(preview)} className={cn(smallButton, 'h-9 disabled:cursor-default disabled:opacity-50')}>
        {already ? `“${preview.label}” is added` : `Add “${preview.label}”`}
      </button>
    </div>
  );
}

export function SegmentDemo() {
  const [rules, setRules] = useState<DemoGroup>(initialRules);
  const [derived, setDerived] = useState<readonly DerivedField[]>([]);
  const [showDerivedForm, setShowDerivedForm] = useState(false);
  const [notice, setNotice] = useState('');

  const catalogue = useMemo(() => buildCatalogue(derived), [derived]);
  const counts = useMemo(() => countRules(rules, catalogue), [rules, catalogue]);
  const suggestions = ageSuggestions(derived);

  const actions = {
    update: (id: string, change: (node: DemoNode) => DemoNode) => setRules((root) => updateNode(root, id, change)),
    remove: (id: string) => setRules((root) => removeNode(root, id)),
    addRule: (groupId: string) =>
      setRules((root) => {
        const field = catalogue.find((candidate) => candidate.key === 'extras.Loyalty tier') ?? catalogue[0];
        if (field === undefined) return root;
        const op = OPERATORS_BY_TYPE[field.type][0] as RuleOperator;
        const rule: DemoRule = { kind: 'rule', id: nextId(root, 'r'), field: field.key, op, value: defaultValue(field, op) };
        return updateNode(root, groupId, (node) => (node.kind === 'group' && node.children.length < 20 ? { ...node, children: [...node.children, rule] } : node));
      }),
    addGroup: (groupId: string) =>
      setRules((root) => {
        const field = catalogue.find((candidate) => candidate.key === 'extras.Loyalty tier') ?? catalogue[0];
        if (field === undefined) return root;
        const group: DemoGroup = {
          kind: 'group',
          id: nextId(root, 'g'),
          join: 'or',
          children: [{ kind: 'rule', id: nextId(root, 'r'), field: field.key, op: 'any_of', value: ['Gold', 'Platinum'] }],
        };
        return updateNode(root, groupId, (node) => (node.kind === 'group' && node.children.length < 20 ? { ...node, children: [...node.children, group] } : node));
      }),
  };

  const addDerived = (field: DerivedField, withRule: boolean): void => {
    setDerived((list) => (list.some((have) => have.key === field.key) ? list : [...list, field]));
    if (withRule) {
      setRules((root) => ({
        ...root,
        children: [...root.children, { kind: 'rule', id: nextId(root, 'r'), field: field.key, op: 'gte', value: 25 }],
      }));
      setNotice(`Added the derived field ${field.label}, and a rule: ${field.label} is at least 25.`);
    } else {
      setNotice(`Added the derived field ${field.label}. It is in the field list under Derived.`);
    }
  };

  const columnFields = catalogue.filter((field) => field.group === 'columns');

  return (
    <div id="segments" className="scroll-mt-24">
      <div className="overflow-hidden rounded-lg border border-line-strong bg-surface-1 shadow-lift">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-warn/7 px-4 py-2.5 text-[0.82rem] text-text sm:px-5">
          <span className="text-muted">
            {formatCount(DEMO_PEOPLE_COUNT)} made-up people, generated in your browser · nothing is sent
          </span>
        </div>

        <div className="border-b border-line px-4 py-4 sm:px-5">
          <p className="font-mono text-[0.64rem] uppercase tracking-[0.13em] text-muted">Every column is a field</p>
          <ul className="mt-2.5 flex flex-wrap gap-1.5" aria-label="Fields from the upload's columns">
            {columnFields.map((field) => (
              <li key={field.key} className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-white/[0.04] px-2.5 py-1 text-[0.8rem] text-text">
                {field.label}
                <span className="font-mono text-[0.66rem] text-muted">
                  {field.type} · {field.coverage}%
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {suggestions.map((field) => (
              <button key={field.key} type="button" className={cn(smallButton, 'border-brand-b/50 bg-brand-a/20 text-brand-ink-strong hover:bg-brand-a/30')} onClick={() => addDerived(field, true)}>
                <span aria-hidden="true">✦</span> Suggested: add “{field.label}” from {field.source}
              </button>
            ))}
            <button type="button" aria-expanded={showDerivedForm} className={smallButton} onClick={() => setShowDerivedForm((open) => !open)}>
              <span aria-hidden="true">＋</span> Add a derived field
            </button>
          </div>
          {showDerivedForm && <DerivedFieldForm existing={derived} onAdd={(field) => addDerived(field, false)} />}
          <p role="status" className="mt-2 min-h-[1.2em] text-[0.8rem] text-good">
            {notice}
          </p>
        </div>

        <div className="grid gap-5 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_300px]">
          <GroupBox group={rules} depth={0} catalogue={catalogue} perNode={counts.perNode} actions={actions} />

          <aside aria-label="Who this segment holds" className="self-start rounded-lg border border-brand-b/30 bg-linear-to-b from-brand-a/12 to-brand-a/3 p-4 lg:sticky lg:top-24">
            <p className="font-mono text-[0.64rem] uppercase tracking-[0.13em] text-brand-ink">Rule segment · keeps itself current</p>
            <div aria-live="polite" aria-atomic="true">
              <p className="mt-2 flex items-baseline gap-2">
                <span className="text-[2.4rem] font-semibold leading-none tracking-tight text-text tabular-nums">{formatCount(counts.matched.length)}</span>
                <span className="text-[0.9rem] text-muted">of {formatCount(DEMO_PEOPLE_COUNT)} people match</span>
              </p>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-[0.82rem]">
                <div className="rounded-base border border-line bg-white/[0.03] px-3 py-2">
                  <dt className="text-muted">Reachable on WhatsApp</dt>
                  <dd className="text-[1.05rem] font-semibold tabular-nums text-text">{formatCount(counts.whatsapp)}</dd>
                </div>
                <div className="rounded-base border border-line bg-white/[0.03] px-3 py-2">
                  <dt className="text-muted">Reachable by email</dt>
                  <dd className="text-[1.05rem] font-semibold tabular-nums text-text">{formatCount(counts.email)}</dd>
                </div>
              </dl>
            </div>
            <p className="mt-4 font-mono text-[0.64rem] uppercase tracking-[0.13em] text-muted">A few of them</p>
            <ul className="mt-2 grid gap-1.5">
              {counts.matched.slice(0, 5).map((person) => (
                <li key={person.id} className="flex items-center justify-between gap-2 text-[0.82rem]">
                  <span className="truncate text-text">{person.name}</span>
                  <span className="shrink-0 font-mono text-[0.7rem] text-muted">{person.maskedEmail ?? (person.hasWhatsApp ? 'WhatsApp only' : 'no address')}</span>
                </li>
              ))}
              {counts.matched.length === 0 && <li className="text-[0.82rem] text-muted">Nobody matches these rules.</li>}
            </ul>
          </aside>
        </div>
      </div>
    </div>
  );
}
